import Webcam from "react-webcam";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import {
  Stack,
  Button,
  Card,
  CardMedia,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { browserName } from "react-device-detect";
import "react-loading-skeleton/dist/skeleton.css";
import { IWebImage } from "../../shared/dtos";
import {
  stageDetailsAPICall,
  webImageBase64APICall,
} from "../../shared/APICalls";
import routesData from "../../shared/routes.json";
import CamStyles from "./WebCam.module.scss";
import ErrorSnackBar from "../SnackBars/ErrorSnackBar";
import SnackBar from "../SnackBars/SnackBar";
import ipvimgdesktop from "../../Components/assets/Images/ipvnewinstructions.svg";
import ipvinstructionsmobile from "../../Components/assets/Images/ipvinstructionsmobile.svg";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import backicon from "../../Components/assets/Images/back.svg";
import { useDispatch } from "react-redux";
import {
  setCompletedStages,
  setCurrentStage,
} from "../redux/actions/pathNameActions";
import TagManager from "react-gtm-module";
import ProgressComp from "../ProgressComp/ProgressComp";
import SideBar from "../SideBar/SideBar";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import MenuIcon from "@mui/icons-material/Menu";
import ProgressBar from "../ProgressBar/ProgressBar";

const DISPLAY_SIZE = 236; // CSS display size for the circular webcam
const CAPTURE_WIDTH = 640; // Higher resolution for face detection + capture
const CAPTURE_HEIGHT = 480;
const CAPTURE_DURATION = 5;

// Temporal filtering: require N consecutive frames passing all checks
// before any frame is scored. A blink lasts ~150ms, a saccade ~50-100ms.
// 3 consecutive good frames at 200ms = 600ms of stable face — catches blinks,
// saccades, and single-frame detection noise while remaining achievable on
// slower mobile devices.
const CONSECUTIVE_GOOD_FRAMES_REQUIRED = 3;

// ─── Face Mesh landmark indices ─────────────────────────────────────────────

// EAR (Eye Aspect Ratio) landmarks
const LEFT_EYE = [33, 160, 158, 133, 153, 144];
const RIGHT_EYE = [362, 385, 387, 263, 373, 380];
const EAR_THRESHOLD = 0.2; // below = eyes closed

// Gaze / face orientation landmarks
const NOSE_TIP = 1;
const LEFT_FACE_EDGE = 234;
const RIGHT_FACE_EDGE = 454;
const FOREHEAD = 10;
const CHIN = 152;
const GAZE_THRESHOLD = 0.1; // reject if nose deviates >10% from face center

type Landmark = { x: number; y: number; z: number };

// ─── Scoring helpers ────────────────────────────────────────────────────────

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function computeEAR(landmarks: Landmark[], indices: number[]): number {
  const [p1, p2, p3, p4, p5, p6] = indices.map((i) => landmarks[i]);
  const horizontal = dist(p1, p4);
  if (horizontal === 0) return 0;
  return (dist(p2, p6) + dist(p3, p5)) / (2 * horizontal);
}

function computeAvgEAR(landmarks: Landmark[]): number {
  return (
    (computeEAR(landmarks, LEFT_EYE) + computeEAR(landmarks, RIGHT_EYE)) / 2
  );
}

function computeGazeScore(landmarks: Landmark[]): number {
  const nose = landmarks[NOSE_TIP];
  const leftEdge = landmarks[LEFT_FACE_EDGE];
  const rightEdge = landmarks[RIGHT_FACE_EDGE];
  const forehead = landmarks[FOREHEAD];
  const chin = landmarks[CHIN];

  // Horizontal (left/right)
  const faceWidth = Math.abs(rightEdge.x - leftEdge.x);
  if (faceWidth === 0) return 0;
  const midX = (leftEdge.x + rightEdge.x) / 2;
  const deviationX = Math.abs(nose.x - midX) / faceWidth;

  // Vertical (up/down)
  const faceHeight = Math.abs(chin.y - forehead.y);
  if (faceHeight === 0) return 0;
  const midY = (forehead.y + chin.y) / 2;
  const deviationY = Math.abs(nose.y - midY) / faceHeight;

  const maxDeviation = Math.max(deviationX, deviationY);
  return Math.max(0, 1 - maxDeviation * 2);
}

function computeSharpness(canvas: HTMLCanvasElement): number {
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;
  const { width: w, height: h } = canvas;
  const { data } = ctx.getImageData(0, 0, w, h);

  const gray = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const idx = i * 4;
    gray[i] = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
  }

  let sum = 0;
  let count = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const lap =
        gray[(y - 1) * w + x] +
        gray[(y + 1) * w + x] +
        gray[y * w + (x - 1)] +
        4 * gray[y * w + x] * -1;
      sum += lap * lap;
      count++;
    }
  }

  return count > 0 ? sum / count : 0;
}

/**
 * Score a frame using its synchronized landmarks.
 * Scoring: sharpness 40%, eye openness 30%, gaze centrality 30%
 */
function scoreFrame(canvas: HTMLCanvasElement, landmarks: Landmark[]): number {
  const eyeAR = computeAvgEAR(landmarks);
  const gazeScore = computeGazeScore(landmarks);
  const sharpness = computeSharpness(canvas);
  return sharpness * 0.4 + eyeAR * 1000 * 0.3 + gazeScore * 1000 * 0.3;
}

// ─── Component ──────────────────────────────────────────────────────────────

const WebcamDemo = (): JSX.Element => {
  const webcamRef = useRef<Webcam>(null);
  const faceMeshRef = useRef<FaceMesh | null>(null);
  const faceMeshReadyRef = useRef(false);
  const [faceMeshFailed, setFaceMeshFailed] = useState(false);

  const capturingRef = useRef(false);
  const bestFrameRef = useRef<{ dataUrl: string; score: number }>({
    dataUrl: "",
    score: -1,
  });
  // Temporal filtering: tracks consecutive frames that all passed quality checks
  const consecutiveGoodFramesRef = useRef(0);
  // Offscreen canvas reused across frames (avoids GC pressure)
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);

  // Cleanup refs for capture timers
  const captureTimersRef = useRef<{
    countdownId: ReturnType<typeof setInterval> | null;
    timeoutId: ReturnType<typeof setTimeout> | null;
  }>({ countdownId: null, timeoutId: null });

  const dispatch = useDispatch();
  const navigationData = routesData;

  const [previewUrl, setPreviewUrl] = useState("");
  const [base64String, setBase64String] = useState("");
  const [allowCapture, setAllowCapture] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [captureHint, setCaptureHint] = useState("");
  const [showCamera, setShowCamera] = useState(true);
  const [isSafari, setisSafari] = useState(false);
  const [border, setBorder] = useState("");
  const [loading, setloading] = useState(false);
  const [rejectMessage, setRejectMessage] = useState("");
  const [errorMessage, seterrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [expandMenu, setExpandMenu] = useState<any>(false);

  const getSystemTheme = () =>
    window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  const [mode, setMode] = useState(getSystemTheme());

  useEffect(() => {
    setMode(getSystemTheme());
  }, [getSystemTheme]);

  // ─── Initialize FaceMesh ──────────────────────────────────────────────────

  useEffect(() => {
    // Create reusable offscreen canvas
    const offscreen = document.createElement("canvas");
    offscreen.width = CAPTURE_WIDTH;
    offscreen.height = CAPTURE_HEIGHT;
    offscreenRef.current = offscreen;

    const faceMesh = new FaceMesh({
      locateFile: (file: string) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });

    faceMesh.setOptions({
      maxNumFaces: 2, // detect up to 2 so we can reject multi-face
      refineLandmarks: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    faceMesh.onResults((results: any) => {
      const faces: Landmark[][] = results.multiFaceLandmarks || [];
      const count = faces.length;
      const landmarks = count === 1 ? faces[0] : null;

      // Live border feedback
      if (count === 1) {
        setAllowCapture(true);
        setBorder("4px solid green");
      } else {
        setAllowCapture(false);
        setBorder("4px solid red");
      }

      // Synchronized frame scoring during capture
      if (!capturingRef.current) return;

      const video = (webcamRef.current as any)
        ?.video as HTMLVideoElement | null;
      if (!video || video.readyState !== 4) return;

      // ── Quality checks (any failure resets the consecutive counter) ──
      if (count !== 1 || !landmarks || landmarks.length < 468) {
        consecutiveGoodFramesRef.current = 0;
        setCaptureHint(
          count === 0 ? "No face detected" : "Multiple faces detected",
        );
        return;
      }

      const ear = computeAvgEAR(landmarks);
      if (ear < EAR_THRESHOLD) {
        consecutiveGoodFramesRef.current = 0;
        setCaptureHint("Keep your eyes open");
        return;
      }

      const gaze = computeGazeScore(landmarks);
      if (gaze < 1 - GAZE_THRESHOLD * 2) {
        consecutiveGoodFramesRef.current = 0;
        setCaptureHint("Look at the camera");
        return;
      }

      // This frame passed all checks
      consecutiveGoodFramesRef.current += 1;

      // Only score after N consecutive good frames (filters out blinks)
      if (consecutiveGoodFramesRef.current < CONSECUTIVE_GOOD_FRAMES_REQUIRED) {
        setCaptureHint("Hold still...");
        return;
      }

      setCaptureHint("Hold still...");

      // Draw video to offscreen canvas (for sharpness)
      const canvas = offscreenRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);

      const score = scoreFrame(canvas, landmarks);
      if (score > bestFrameRef.current.score) {
        const wc = webcamRef.current as any;
        const dataUrl = wc?.getScreenshot();
        if (dataUrl) {
          bestFrameRef.current = { dataUrl, score };
        }
      }
    });

    faceMeshRef.current = faceMesh;

    faceMesh
      .initialize()
      .then(() => {
        faceMeshReadyRef.current = true;
      })
      .catch(() => {
        setFaceMeshFailed(true);
        setAllowCapture(true); // allow basic capture without face detection
        setBorder("");
      });

    // Feed webcam frames to FaceMesh at ~5 fps
    const intervalId = setInterval(async () => {
      const video = (webcamRef.current as any)
        ?.video as HTMLVideoElement | null;
      if (
        video &&
        video.readyState === 4 &&
        faceMeshRef.current &&
        faceMeshReadyRef.current
      ) {
        try {
          await faceMeshRef.current.send({ image: video });
        } catch {
          // not ready yet
        }
      }
    }, 200);

    return () => {
      clearInterval(intervalId);
      faceMeshRef.current?.close();
      faceMeshRef.current = null;
      faceMeshReadyRef.current = false;
      // Cleanup any running capture timers
      if (captureTimersRef.current.countdownId)
        clearInterval(captureTimersRef.current.countdownId);
      if (captureTimersRef.current.timeoutId)
        clearTimeout(captureTimersRef.current.timeoutId);
    };
  }, []);

  // ─── Stage details + camera permission ────────────────────────────────────

  useEffect(() => {
    window.scrollTo(0, 0);

    stageDetailsAPICall({ stage: "ipv" })
      .then((response: any) => {
        const responseData = response.data;
        dispatch(setCompletedStages(responseData.data.completed_stages));
        dispatch(setCurrentStage(responseData.data.current_stage));
        if (responseData.data.ErrorMessage) {
          setRejectMessage(responseData.data.ErrorMessage);
        }
        const retakeImage = sessionStorage.getItem("re-take");

        if (retakeImage === "retake") {
          setPreviewUrl("");
          setBase64String("");
        } else {
          setPreviewUrl(
            responseData.data.data?.img_base64
              ? `data:image/gif;base64,${responseData.data.data.img_base64}`
              : "",
          );
          setBase64String(
            responseData.data.data?.img_base64
              ? responseData.data.data.img_base64
              : "",
          );
        }
      })
      .catch(() => {});

    let intervalId: any;
    let alertShown = false;

    const startCameraInterval = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        stream.getTracks().forEach((track) => track.stop());
        setShowCamera(true);
        clearInterval(intervalId);
      } catch (error: any) {
        if (browserName.includes("Safari")) {
          setisSafari(true);
        }
        if (
          !alertShown &&
          (error.name === "NotAllowedError" ||
            error.name === "PermissionDeniedError")
        ) {
          alertShown = true;
          const shouldAskForPermission = window.confirm(
            "Camera access is blocked. Please unblock it in browser settings.",
          );
          if (shouldAskForPermission) {
            intervalId = setInterval(async () => {
              try {
                const stream = await navigator.mediaDevices.getUserMedia({
                  video: true,
                });
                stream.getTracks().forEach((track) => track.stop());
                clearInterval(intervalId);
                window.location.reload();
              } catch {
                // still blocked
              }
            }, 1000);
          }
        }
      }
    };

    if (browserName !== "Firefox") {
      intervalId = setInterval(startCameraInterval, 1000);
    }
    return () => clearInterval(intervalId);
  }, []);

  // ─── Capture ──────────────────────────────────────────────────────────────

  const capture = useCallback(() => {
    if (isCapturing) return;

    // Fallback: if FaceMesh failed to load, take a single screenshot instantly
    if (faceMeshFailed) {
      const wc = webcamRef.current as any;
      const imageSrc = wc?.getScreenshot();
      if (imageSrc) setPreviewUrl(imageSrc);
      return;
    }

    setIsCapturing(true);
    setCountdown(CAPTURE_DURATION);
    setCaptureHint("Look at the camera and keep eyes open");
    bestFrameRef.current = { dataUrl: "", score: -1 };
    consecutiveGoodFramesRef.current = 0;
    capturingRef.current = true;

    const countdownId = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const timeoutId = setTimeout(() => {
      capturingRef.current = false;
      setIsCapturing(false);
      setCountdown(0);
      setCaptureHint("");
      captureTimersRef.current = { countdownId: null, timeoutId: null };

      // Tiered fallback: never block the user
      if (bestFrameRef.current.dataUrl) {
        // Tier 1/2: A scored frame exists
        setPreviewUrl(bestFrameRef.current.dataUrl);
      } else {
        // Tier 3: FaceMesh never produced a usable frame — raw screenshot fallback
        const wc = webcamRef.current as any;
        const fallback = wc?.getScreenshot();
        if (fallback) {
          setPreviewUrl(fallback);
        } else {
          // Truly stuck: webcam ref broken
          seterrorMessage("Camera not ready, please try again.");
          setTimeout(() => seterrorMessage(""), 5000);
        }
      }
    }, CAPTURE_DURATION * 1000);

    captureTimersRef.current = { countdownId, timeoutId };
  }, [isCapturing, faceMeshFailed]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function webImageBase64() {
    TagManager.dataLayer({
      dataLayer: { event: "formSubmitIpv" },
    });

    setloading(true);
    const obj: IWebImage = {
      ipv_base64: base64String !== "" ? base64String : previewUrl,
    };

    webImageBase64APICall(obj)
      .then((response: any) => {
        const responseData = response.data;
        setloading(false);
        if (
          responseData.data.next_stage &&
          navigationData.hasOwnProperty(responseData.data.next_stage)
        ) {
          window.location.href = navigationData[responseData.data.next_stage];
        }
      })
      .catch((error: any) => {
        setloading(false);
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
          setTimeout(() => setMessage(""), 5000);
        }
      });
  }

  const handleClick = () => {
    sessionStorage.setItem("re-take", "");
    if (previewUrl) {
      webImageBase64();
    } else {
      seterrorMessage("Kindly capture your Image");
      setTimeout(() => seterrorMessage(""), 3000);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <SnackBar message={message} />
      <ErrorSnackBar message={rejectMessage} />
      <SnackBar message={errorMessage} />

      <div className={CamStyles.layout}>
        <div className={CamStyles.sidebar}>
          <SideBar />
        </div>
        <div className={CamStyles.pageContent}>
          <div className={CamStyles.main}>
            <div className={CamStyles.mainWrapper}>
              <div className={CamStyles.wrapper}>
                <div className={CamStyles.headerSection}>
                  <p className={CamStyles.signup}>
                    <div className={CamStyles.mobileBack}>
                      <ArrowBackIosIcon />
                    </div>
                    Verify your identity
                    <div
                      className={CamStyles.mobileMenu}
                      onClick={() => {
                        setExpandMenu(!expandMenu);
                      }}
                    >
                      {expandMenu ? <CloseIcon /> : <MenuIcon />}
                    </div>
                  </p>
                  <ProgressBar />
                  <div
                    className={CamStyles.mobileexpandedMenu}
                    style={{
                      height: expandMenu ? "100%" : "0%",
                    }}
                  >
                    <div className={CamStyles.mobilemenuContent}>
                      <SideBar />
                    </div>
                  </div>
                  <p className={CamStyles.subText}>
                    Capture your live photo for verification.
                  </p>
                </div>

                <div className={CamStyles.ipvWrap}>
                  <div className={CamStyles.Container}>
                    <div
                      style={{
                        width: DISPLAY_SIZE,
                        height: DISPLAY_SIZE,
                        position: "relative",
                      }}
                    >
                      <div>
                        {previewUrl === "" ? (
                          <>
                            <Webcam
                              audio={false}
                              ref={webcamRef}
                              forceScreenshotSourceSize
                              screenshotFormat="image/png"
                              videoConstraints={{
                                width: CAPTURE_WIDTH,
                                height: CAPTURE_HEIGHT,
                                facingMode: "user",
                              }}
                              style={{
                                height: DISPLAY_SIZE,
                                width: DISPLAY_SIZE,
                                borderRadius: "50%",
                                position: "absolute",
                                border: border,
                                marginTop: "10px",
                                objectFit: "cover",
                              }}
                            />
                            {isCapturing && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "10px",
                                  left: 0,
                                  width: DISPLAY_SIZE,
                                  height: DISPLAY_SIZE,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                                  zIndex: 10,
                                }}
                              >
                                <CircularProgress
                                  variant="determinate"
                                  value={
                                    ((CAPTURE_DURATION - countdown) /
                                      CAPTURE_DURATION) *
                                    100
                                  }
                                  size={60}
                                  thickness={4}
                                  sx={{ color: "#fff" }}
                                />
                                <Typography
                                  sx={{
                                    color: "#fff",
                                    fontSize: "22px",
                                    fontWeight: 700,
                                    position: "absolute",
                                  }}
                                >
                                  {countdown}
                                </Typography>
                              </Box>
                            )}
                          </>
                        ) : (
                          <div>
                            <Card
                              sx={{
                                width: DISPLAY_SIZE,
                                height: DISPLAY_SIZE,
                                marginTop: "10px",
                                borderRadius: "50%",
                                zIndex: 100,
                                background: "white",
                                marginLeft: "1px",
                                overflow: "hidden",
                              }}
                            >
                              <CardMedia
                                component="img"
                                height={DISPLAY_SIZE}
                                image={previewUrl}
                                sx={{
                                  zIndex: 100,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            </Card>
                            <Stack
                              width={"150px"}
                              style={{ margin: "10px 0" }}
                            ></Stack>
                          </div>
                        )}
                      </div>
                    </div>
                    <Stack
                      alignItems={"center"}
                      sx={{ marginTop: { xs: "20px", sm: "10px" } }}
                      height={"35px"}
                    >
                      {isCapturing && captureHint ? (
                        <Typography
                          sx={{
                            color:
                              captureHint === "Hold still..."
                                ? "#049981"
                                : "#e65100",
                            fontWeight: 500,
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          {captureHint}
                        </Typography>
                      ) : !allowCapture && !isCapturing ? (
                        <Typography
                          style={{
                            color: "red",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          No Face/ Detected too many faces
                        </Typography>
                      ) : (
                        ""
                      )}
                    </Stack>

                    <Stack>
                      {showCamera ? (
                        <Stack alignItems={"center"}>
                          {previewUrl === "" ? (
                            <div className={CamStyles.buttonStack}>
                              <Button
                                // sx={{
                                //   height: { xs: "60px", sm: "50px" },
                                //   width: { xs: "100%", sm: "187px" },
                                // }}
                                className={CamStyles.takePhoto}
                                variant="contained"
                                onClick={capture}
                                disabled={!allowCapture || isCapturing}
                              >
                                {isCapturing
                                  ? `Capturing... ${countdown}s`
                                  : "Capture Photo"}
                              </Button>
                            </div>
                          ) : (
                            <div className={CamStyles.buttonStack}>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  sessionStorage.setItem("re-take", "retake");
                                  window.location.reload();
                                }}
                                style={{
                                  color: "var(--ds-text-primary)",
                                }}
                                className={CamStyles.buttonStyles}
                              >
                                Re-Take Photo
                              </Button>
                            </div>
                          )}
                        </Stack>
                      ) : (
                        <Stack alignItems={"center"} justifyContent={"center"}>
                          <Typography>
                            Grant the Camera access to proceed further
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </div>
                  <div className={CamStyles.instructionSection}>
                    <div className={CamStyles.textdiv}>
                      <p className={CamStyles.instructiontext}>
                        <DoneOutlinedIcon style={{ color: "#049981" }} /> Make
                        sure you are in a well-lit space and your face is
                        clearly visible.
                      </p>
                      <p className={CamStyles.instructiontext}>
                        <DoneOutlinedIcon style={{ color: "#049981" }} />
                        Please remove any spectacles, hats, and masks.
                      </p>
                    </div>
                    <div className={CamStyles.imagetitle}>
                      <div className={CamStyles.divider}></div>
                      Photo Verification Guidelines
                      <div className={CamStyles.divider}></div>
                    </div>
                    <div className={CamStyles.instructionsImage}>
                      <img
                        src={ipvinstructionsmobile}
                        alt=""
                        className={CamStyles.insImageMobile}
                        // style={{
                        //   filter:
                        //     mode === "dark"
                        //       ? "grayscale(0.9) invert(0.9) brightness(1)"
                        //       : "",
                        // }}
                      />
                      <img
                        src={ipvimgdesktop}
                        alt=""
                        className={CamStyles.insImage}
                        // style={{
                        //   filter:
                        //     mode === "dark"
                        //       ? "grayscale(0.9) invert(0.9) brightness(1)"
                        //       : "",
                        // }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Stack>
                <Stack alignItems={"center"}>
                  <div className={CamStyles.buttonSection}>
                    <div className={CamStyles.buttonSectionRight}>
                      <Button
                        variant="text"
                        sx={{
                          color: "var(--ds-text-primary)",
                          gap: "8px",
                        }}
                        onClick={() => {
                          window.location.href = "/uploaddocuments";
                        }}
                        className={CamStyles.backbutton}
                      >
                        <KeyboardBackspaceIcon /> Back
                      </Button>
                      {previewUrl !== "" && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            sessionStorage.setItem("re-take", "retake");
                            window.location.reload();
                          }}
                          className={CamStyles.retakebutton}
                          style={{ color: "var(--ds-text-primary)" }}
                        >
                          Re-Take Photo
                        </Button>
                      )}
                      {previewUrl === "" && (
                        <Button
                          // sx={{
                          //   height: { xs: "60px", sm: "50px" },
                          //   width: { xs: "100%", sm: "187px" },
                          // }}
                          className={CamStyles.takePhotoMobile}
                          variant="contained"
                          onClick={capture}
                          disabled={!allowCapture || isCapturing}
                        >
                          {isCapturing
                            ? `Capturing... ${countdown}s`
                            : "Capture Photo"}
                        </Button>
                      )}
                      {previewUrl !== "" && (
                        <LoadingButton
                          variant="contained"
                          onClick={handleClick}
                          loading={loading}
                          disabled={previewUrl === ""}
                          className={CamStyles.continueButtonMobile}
                        >
                          Continue
                        </LoadingButton>
                      )}
                      <LoadingButton
                        variant="contained"
                        onClick={handleClick}
                        loading={loading}
                        disabled={previewUrl === ""}
                        className={CamStyles.continueButton}
                      >
                        Continue
                      </LoadingButton>
                    </div>
                  </div>
                </Stack>
              </Stack>

              {isSafari && (
                <Stack alignItems={"center"} justifyContent={"center"}>
                  <Typography sx={{ textAlign: "center" }}>
                    Please Press Reload Button after granting camera access
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                    style={{ color: "#006779" }}
                  >
                    Reload
                  </Button>
                </Stack>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WebcamDemo;
